import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';

import { AgeingService, API_BASE_URL } from '../../../../service/proxy';
import { AgeingSheetSelectionComponent } from './steps/sheet-selection/ageing-sheet-selection.component';
import { AgeingRangeSelectionComponent } from './steps/range-mapping/ageing-range-selection.component';
import { AgeingColumnSelectionComponent } from './steps/column-mapping/ageing-column-selection.component';
import { AgeingImportDataComponent } from './steps/import-data/ageing-import-data.component';

import { WizardStep } from '../../../shared/controls/wizard-control/wizard-step.interface';
import { WizardStepComponent } from '../../../shared/controls/wizard-control/wizard-step.component';
import { WizardControlComponent } from '../../../shared/controls/wizard-control/wizard-control.component';

import { ToastService } from '../../../shared/toast/toast.service';
import { ErrorService } from '../../../shared/services/error-service';
import { Inject, Optional } from '@angular/core';

@Component({
    selector: 'ageing-wizard',
    templateUrl: './ageing-wizard.component.html',
    styleUrl: './ageing-wizard.component.scss',

    imports: [
        CommonModule,
        FormsModule,

        WizardControlComponent,
        WizardStepComponent,

        AgeingSheetSelectionComponent,
        AgeingRangeSelectionComponent,
        AgeingColumnSelectionComponent,
        AgeingImportDataComponent,
    ],

    providers: [
        AgeingService
    ]
})
export class AgeingWizardComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('sheetSelectionComponent')
    sheetSelectionComponent!: WizardStep;

    @ViewChild('rowSelectionComponent')
    rowSelectionComponent!: WizardStep;

    @ViewChild('columnSelectionComponent')
    columnSelectionComponent!: WizardStep;

    @ViewChild('importComponent')
    importComponent!: WizardStep;

    public batchId: number = 0;
    public viewInitialized: boolean = false;
    public completing: boolean = false;

    private pollingTimer: any = null;
    private baseUrl: string;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private http: HttpClient,
        private ageingService: AgeingService,
        private errorService: ErrorService,
        private toastService: ToastService,
        private cdr: ChangeDetectorRef,
        @Optional() @Inject(API_BASE_URL) baseUrl?: string,
    ) {
        this.baseUrl = baseUrl ?? '';
    }

    ngOnInit(): void {
        this.route.params.subscribe(params => {
            this.batchId = +params['id'];
        });
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.viewInitialized = true;
            this.cdr.detectChanges();
        });
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

    isWorkSheetSelectionValid(): boolean {
        if (!this.viewInitialized) return true;
        return this.sheetSelectionComponent?.isValid() || false;
    }

    isRowSelectionValid(): boolean {
        if (!this.viewInitialized) return true;
        return this.rowSelectionComponent?.isValid() || false;
    }

    isColumnSelectionValid(): boolean {
        if (!this.viewInitialized) return true;
        return this.columnSelectionComponent?.isValid() || false;
    }

    isImportValid(): boolean {
        if (!this.viewInitialized) return true;
        return this.importComponent?.isValid() || false;
    }

    finished(): void {
        this.completing = true;
        this.ageingService.completeAgeingBatch(this.batchId)
            .subscribe({
                next: () => {
                    this.startPolling();
                },
                error: (error) => {
                    this.completing = false;
                    this.errorService.handleError(error);
                },
            }
        );
    }

    private startPolling(): void {
        this.pollingTimer = setInterval(() => {
            this.http.get<any>(`${this.baseUrl}/ageing/${this.batchId}/completion-status`)
                .subscribe({
                    next: (result) => {
                        if (result.status === 'completed') {
                            this.stopPolling();
                            this.completing = false;
                            this.toastService.show('Ageing batch completed successfully.', 'Success');
                            this.router.navigate([`/ageing`]);
                        } else if (result.status === 'failed') {
                            this.stopPolling();
                            this.completing = false;
                            this.toastService.show(`Completion failed: ${result.error}`, 'Error');
                        }
                    },
                    error: () => {
                        // Polling error — keep trying
                    }
                });
        }, 5000);
    }

    private stopPolling(): void {
        if (this.pollingTimer) {
            clearInterval(this.pollingTimer);
            this.pollingTimer = null;
        }
    }

    onCancel(): void {
        this.router.navigate([`/ageing`]);
    }
}
